import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

type HttpResponseLike = { ok: boolean; status: number; url: string };

// A thrown fetch Response reaches Sentry as "[object Response]", no stacktrace.
const isHttpResponseLike = (value: unknown): value is HttpResponseLike =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as HttpResponseLike).status === 'number' &&
  typeof (value as HttpResponseLike).url === 'string' &&
  typeof (value as HttpResponseLike).ok === 'boolean';

const getPathname = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const normalizeException = (
  e: unknown,
): { error: unknown; tags?: Record<string, string> } => {
  if (e instanceof Error) {
    return { error: e };
  }

  if (isHttpResponseLike(e)) {
    // Query string dropped: it can hold tokens.
    const pathname = getPathname(e.url);
    const error = new Error(`HTTP ${e.status} ${pathname}`);
    error.name = 'HttpError';

    return {
      error,
      tags: {
        http_status: String(e.status),
        http_path: pathname,
      },
    };
  }

  if (typeof e === 'string') {
    return { error: new Error(e) };
  }

  return { error: e };
};

class ErrorLogger {
  isInitialized = false;

  init({
    userId,
    sentryDsn,
  }: {
    userId: string | null;
    sentryDsn: string;
  }): void {
    Sentry.init({
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      dsn: sentryDsn,
      // Session Replay is deliberately absent: it is billed per recording, the
      // included allowance is small, and this app generated far more than that
      // -- draining the org-wide pay-as-you-go budget that errors are paid
      // from. Nobody on the team watched the recordings. The integration is
      // left out rather than set to 0 so the replay bundle is tree-shaken away.
      //
      // Spans are billed individually and come out of that same shared budget,
      // hence the low trace rate. Sentry's quota is org-wide and spent
      // first-come-first-served, so telemetry nobody reads starves what we do.
      tracesSampleRate: 0.03,
    });
    this.isInitialized = true;
    this.setUser(userId);
  }

  captureException(e: unknown, description?: string): void {
    if (this.isInitialized) {
      const { error, tags } = normalizeException(e);

      Sentry.captureException(error, {
        tags,
        extra: description ? { description } : undefined,
      });
    }
    if (description) {
      console.error(`${description}: `, e);
    } else {
      console.error(e);
    }
  }

  setUser(userId: string | null): void {
    Sentry.setUser(userId ? { id: userId } : null);
  }
}

const errorLogger = new ErrorLogger();

export default errorLogger;
